import { createClient } from '@supabase/supabase-js';
import '@tensorflow/tfjs';
import * as use from '@tensorflow-models/universal-sentence-encoder';
import type { Flag } from '../lib/types';

// Initialize Supabase client using Vite environment variables
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

interface ModerationMetadata {
  timestamp: string;
  type: string;
  [key: string]: string | number | boolean;
}

export class DocumentProcessor {
  private static model: use.UniversalSentenceEncoder | null = null;
  private static initialized = false;
  private static initializationPromise: Promise<void> | null = null;
  private static readonly MAX_RETRIES = 3;
  private static readonly RETRY_DELAY = 5000;
  private static readonly LOAD_TIMEOUT = 30000; // 30 seconds timeout
  private static readonly VERIFY_TIMEOUT = 10000; // 10 seconds for verification

  /**
   * Format embedding for Supabase vector type
   */
  private static formatEmbeddingForSupabase(embedding: number[]): string {
    // Ensure all values are valid numbers and format them to 6 decimal places
    const validEmbedding = embedding.map(value => {
      if (isNaN(value) || !isFinite(value)) {
        console.warn('Invalid embedding value found, replacing with 0');
        return 0;
      }
      return Number(value.toFixed(6));
    });
    
    // Convert to string with comma-separated values and no spaces
    return `[${validEmbedding.join(',')}]`;
  }

  /**
   * Initialize the embedding model
   */
  static async initialize() {
    if (this.initialized) return;
    if (this.initializationPromise) return this.initializationPromise;

    this.initializationPromise = (async () => {
      let retries = 0;
      while (retries < this.MAX_RETRIES) {
        try {
          console.log(`Loading embedding model (attempt ${retries + 1}/${this.MAX_RETRIES})...`);
          
          // Clear any existing model
          if (this.model) {
            this.model = null;
          }

          // Create a promise that rejects after timeout
          const timeoutPromise = new Promise<never>((_, reject) => {
            setTimeout(() => {
              reject(new Error(`Model loading timed out after ${this.LOAD_TIMEOUT/1000} seconds`));
            }, this.LOAD_TIMEOUT);
          });

          // Load the Universal Sentence Encoder with timeout
          console.log('Starting model download...');
          const loadPromise = use.load() as Promise<use.UniversalSentenceEncoder>;
          this.model = await Promise.race([loadPromise, timeoutPromise]);
          
          if (!this.model) {
            throw new Error('Model failed to load - model is null');
          }

          console.log('Model downloaded, starting verification...');
          
          // Verify the model is properly loaded with a separate timeout
          try {
            const verifyTimeout = new Promise<never>((_, reject) => {
              setTimeout(() => {
                reject(new Error(`Model verification timed out after ${this.VERIFY_TIMEOUT/1000} seconds`));
              }, this.VERIFY_TIMEOUT);
            });

            const verifyPromise = (async () => {
              console.log('Generating test embedding...');
              const testEmbedding = await this.model!.embed(['test']);
              console.log('Test embedding generated, converting to array...');
              const embeddingArray = await testEmbedding.array();
              console.log('Embedding array created, checking validity...');
              
              if (!embeddingArray || embeddingArray.length === 0 || !embeddingArray[0] || embeddingArray[0].length === 0) {
                throw new Error('Model test failed - invalid embedding output');
              }
              
              console.log('Embedding array is valid');
              return embeddingArray[0];
            })();

            await Promise.race([verifyPromise, verifyTimeout]);
            console.log('Model verification successful');
          } catch (testError) {
            console.error('Model test failed:', testError);
            throw new Error(`Model initialization test failed: ${testError instanceof Error ? testError.message : 'Unknown error'}`);
          }

          console.log('Embedding model loaded and verified successfully!');
          this.initialized = true;
          return;
        } catch (error) {
          console.error(`Error initializing embedding model (attempt ${retries + 1}):`, error);
          retries++;
          
          if (retries === this.MAX_RETRIES) {
            this.initialized = false;
            this.initializationPromise = null;
            throw new Error(`Failed to initialize embedding model after ${this.MAX_RETRIES} attempts: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
          
          // Wait before retrying with exponential backoff
          const delay = this.RETRY_DELAY * Math.pow(2, retries - 1);
          console.log(`Waiting ${delay/1000} seconds before retrying...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    })();

    return this.initializationPromise;
  }

  /**
   * Generate embeddings using local model
   */
  static async generateEmbedding(text: string): Promise<string> {
    if (!this.initialized) {
      await this.initialize();
    }

    if (!this.model) {
      throw new Error('Embedding model not initialized');
    }

    try {
      console.log('Generating embedding...');
      const embeddings = await this.model.embed([text]);
      console.log('Embedding generated, converting to array...');
      const embeddingArray = await embeddings.array();
      console.log('Embedding array created, formatting for Supabase...');
      const formattedEmbedding = this.formatEmbeddingForSupabase(embeddingArray[0]);
      console.log('Embedding formatted for Supabase');
      return formattedEmbedding;
    } catch (error) {
      console.error('Error generating embedding:', error);
      throw error;
    }
  }

  /**
   * Store moderation result in Supabase
   */
  static async storeModerationResult(data: {
    content: string;
    flags: Flag[];
    overallToxicity: number;
    metadata: ModerationMetadata;
  }) {
    try {
      // Generate embedding for the content
      const embedding = await this.generateEmbedding(data.content);
      
      // Store in Supabase
      const { error } = await supabase
        .from('moderation_results')
        .insert({
          content: data.content,
          embedding,
          flags: data.flags,
          overall_toxicity: data.overallToxicity,
          metadata: data.metadata
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error storing moderation result:', error);
      throw error;
    }
  }

  /**
   * Search for similar past moderation results
   */
  static async searchSimilarModerations(content: string, limit: number = 5) {
    try {
      // Generate embedding for the content
      const contentEmbedding = await this.generateEmbedding(content);
      
      // Search in Supabase for similar moderation results
      const { data, error } = await supabase.rpc('match_moderation_results', {
        query_embedding: contentEmbedding,
        match_threshold: 0.7,
        match_count: limit
      });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting similar moderations:', error);
      throw error;
    }
  }
} 